import { Module } from 'vuex';
import store from '@/libs/core/+state/store';
import { isFunction } from 'lodash';
import { ListLayoutState, ListLayoutViewType, SavedFilter, TableState } from '../core/list-layout.types';
import {
    loadStateFromLocalStorage,
    saveStateToLocalStorage,
    createDefaultState,
} from '../core/list-layout.state';

export interface IListLayoutStore {
    dispatch(action: ListLayoutActionType, payload: any): void;
    useGetter(getter: ListLayoutGetterType): any;
}

export enum ListLayoutActionType {
    Initialize = 'initialize',
    SetActiveView = 'setActiveView',
    SetFilters = 'setFilters',
    SetFilter = 'setFilter',
    ClearFilters = 'clearFilters',
    SaveFilter = 'saveFilter',
    DeleteSavedFilter = 'deleteSavedFilter',
    LoadSavedFilter = 'loadSavedFilter',
    SetTableState = 'setTableState',
    SetKanbanState = 'setKanbanState',
    ToggleFilters = 'toggleFilters',
    Reset = 'reset',
}

export enum ListLayoutGetterType {
    State = 'state',
    ActiveView = 'activeView',
    Filters = 'filters',
    SavedFilters = 'savedFilters',
    TableState = 'tableState',
    KanbanState = 'kanbanState',
    ShowFilters = 'showFilters',
}

const registerListLayoutStore = (storageKey: string, defaultView: ListLayoutViewType): IListLayoutStore => {
    const moduleName = `ListLayout_${storageKey}`;

    // Check if already registered
    if (store.hasModule(moduleName)) {
        return {
            dispatch: (action: ListLayoutActionType, payload: any) => {
                store.dispatch(`${moduleName}/${action}`, payload);
            },
            useGetter: (getter: ListLayoutGetterType) => {
                const val = store.getters[`${moduleName}/${getter}`];
                return isFunction(val) ? val() : val;
            },
        };
    }

    const storeObject: Module<ListLayoutState, unknown> = {
        namespaced: true,
        state: loadStateFromLocalStorage(storageKey, defaultView),
        actions: {
            [ListLayoutActionType.Initialize]: ({ commit, state }, payload: { storageKey: string; defaultView: ListLayoutViewType }) => {
                const loadedState = loadStateFromLocalStorage(payload.storageKey, payload.defaultView);
                commit('setState', loadedState);
            },
            [ListLayoutActionType.SetActiveView]: ({ commit, state }, view: ListLayoutViewType) => {
                commit('setActiveView', view);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.SetFilters]: ({ commit, state }, filters: Record<string, any>) => {
                commit('setFilters', filters);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.SetFilter]: ({ commit, state }, payload: { key: string; value: any }) => {
                commit('setFilter', payload);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.ClearFilters]: ({ commit, state }) => {
                commit('clearFilters');
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.SaveFilter]: ({ commit, state }, filter: SavedFilter) => {
                commit('saveFilter', filter);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.DeleteSavedFilter]: ({ commit, state }, filterId: string) => {
                commit('deleteSavedFilter', filterId);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.LoadSavedFilter]: ({ commit, state }, filterId: string) => {
                commit('loadSavedFilter', filterId);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.SetTableState]: ({ commit, state }, tableState: Partial<TableState>) => {
                commit('setTableState', tableState);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.SetKanbanState]: ({ commit, state }, kanbanState: any) => {
                commit('setKanbanState', kanbanState);
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.ToggleFilters]: ({ commit, state }) => {
                commit('toggleFilters');
                saveStateToLocalStorage(storageKey, state);
            },
            [ListLayoutActionType.Reset]: ({ commit, state }) => {
                const defaultState = createDefaultState(defaultView);
                commit('setState', defaultState);
                saveStateToLocalStorage(storageKey, state);
            },
        },
        mutations: {
            setState: (state, payload: ListLayoutState) => {
                Object.assign(state, payload);
            },
            setActiveView: (state, view: ListLayoutViewType) => {
                state.activeView = view;
            },
            setFilters: (state, filters: Record<string, any>) => {
                state.filters = { ...filters };
            },
            setFilter: (state, payload: { key: string; value: any }) => {
                state.filters = {
                    ...state.filters,
                    [payload.key]: payload.value,
                };
            },
            clearFilters: (state) => {
                state.filters = {};
            },
            saveFilter: (state, filter: SavedFilter) => {
                const existingIndex = state.savedFilters.findIndex((f) => f.id === filter.id);
                if (existingIndex >= 0) {
                    state.savedFilters[existingIndex] = filter;
                } else {
                    state.savedFilters.push(filter);
                }
            },
            deleteSavedFilter: (state, filterId: string) => {
                state.savedFilters = state.savedFilters.filter((f) => f.id !== filterId);
            },
            loadSavedFilter: (state, filterId: string) => {
                const filter = state.savedFilters.find((f) => f.id === filterId);
                if (filter) {
                    state.filters = { ...filter.filters };
                }
            },
            setTableState: (state, tableState: Partial<TableState>) => {
                state.tableState = {
                    ...state.tableState,
                    ...tableState,
                };
            },
            setKanbanState: (state, kanbanState: any) => {
                state.kanbanState = {
                    ...state.kanbanState,
                    ...kanbanState,
                };
            },
            toggleFilters: (state) => {
                state.showFilters = !state.showFilters;
            },
        },
        getters: {
            [ListLayoutGetterType.State]: (state): ListLayoutState => state,
            [ListLayoutGetterType.ActiveView]: (state): ListLayoutViewType => state.activeView,
            [ListLayoutGetterType.Filters]: (state): Record<string, any> => state.filters,
            [ListLayoutGetterType.SavedFilters]: (state): SavedFilter[] => state.savedFilters,
            [ListLayoutGetterType.TableState]: (state): TableState => state.tableState,
            [ListLayoutGetterType.KanbanState]: (state): any => state.kanbanState,
            [ListLayoutGetterType.ShowFilters]: (state): boolean => state.showFilters,
        },
    };

    store.registerModule(moduleName, storeObject);

    return {
        dispatch: (action: ListLayoutActionType, payload: any) => {
            store.dispatch(`${moduleName}/${action}`, payload);
        },
        useGetter: (getter: ListLayoutGetterType) => {
            const val = store.getters[`${moduleName}/${getter}`];
            return isFunction(val) ? val() : val;
        },
    };
};

export function createListLayoutStore(storageKey: string, defaultView: ListLayoutViewType = ListLayoutViewType.Table): IListLayoutStore {
    return registerListLayoutStore(storageKey, defaultView);
}
